import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '@healthdesk/shared-services';
import { DDX_INGEST_COLLECTIONS } from '@healthdesk/shared-utils';

/**
 * After a Firestore listen stream errors (e.g. WebChannel transport), the SDK does not
 * keep delivering updates until a new listener is attached. Re-subscribe with backoff.
 * @param {() => void} attach
 * @param {(err: unknown) => void} onError
 * @returns {{ clearRetry: () => void, onFailure: (err: unknown) => void }}
 */
function scheduleListenRetry(attach, onError) {
  let attempt = 0;
  let retryTimer = null;

  const clearRetry = () => {
    if (retryTimer != null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    attempt = 0;
  };

  const onFailure = (err) => {
    onError(err);
    if (retryTimer != null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    const delayMs = Math.min(30000, 1000 * Math.pow(2, Math.min(attempt, 5)));
    attempt += 1;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      attach();
    }, delayMs);
  };

  return { clearRetry, onFailure };
}

/**
 * @param {string | null | undefined} uid
 */
export function useDdxIngestJobs(uid) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(!!uid);
  const [error, setError] = useState(null);
  const [listenReconnecting, setListenReconnecting] = useState(false);
  const unsubRef = useRef(null);

  useEffect(() => {
    if (!uid) {
      setJobs([]);
      setLoading(false);
      setError(null);
      setListenReconnecting(false);
      return undefined;
    }
    setLoading(true);
    let cancelled = false;
    let retry = { clearRetry: () => {}, onFailure: () => {} };

    const attach = () => {
      if (cancelled) return;
      try {
        if (unsubRef.current) {
          unsubRef.current();
          unsubRef.current = null;
        }
      } catch (e) {
        /* ignore */
      }

      const q = db
        .collection(DDX_INGEST_COLLECTIONS.JOBS)
        .where('createdByUid', '==', uid)
        .orderBy('createdAt', 'desc')
        .limit(50);

      unsubRef.current = q.onSnapshot(
        (snap) => {
          if (cancelled) return;
          retry.clearRetry();
          setListenReconnecting(false);
          setJobs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
          setError(null);
        },
        (err) => {
          if (cancelled) return;
          console.warn('[useDdxIngestJobs] Firestore listen error (will retry)', err?.code, err?.message);
          setListenReconnecting(true);
          setLoading(false);
          setError(err);
          try {
            if (unsubRef.current) {
              unsubRef.current();
              unsubRef.current = null;
            }
          } catch (e) {
            /* ignore */
          }
          retry.onFailure(err);
        }
      );
    };

    retry = scheduleListenRetry(attach, () => {});
    attach();

    return () => {
      cancelled = true;
      retry.clearRetry();
      try {
        if (unsubRef.current) {
          unsubRef.current();
          unsubRef.current = null;
        }
      } catch (e) {
        /* ignore */
      }
    };
  }, [uid]);

  return { jobs, loading, error, listenReconnecting };
}

/**
 * @param {string | null | undefined} jobId
 */
export function useDdxIngestStagedRows(jobId) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(!!jobId);
  const [error, setError] = useState(null);
  const [listenReconnecting, setListenReconnecting] = useState(false);
  const unsubRef = useRef(null);

  useEffect(() => {
    if (!jobId) {
      setRows([]);
      setLoading(false);
      setError(null);
      setListenReconnecting(false);
      return undefined;
    }
    setLoading(true);
    let cancelled = false;
    let retry = { clearRetry: () => {}, onFailure: () => {} };

    const attach = () => {
      if (cancelled) return;
      try {
        if (unsubRef.current) {
          unsubRef.current();
          unsubRef.current = null;
        }
      } catch (e) {
        /* ignore */
      }

      const q = db
        .collection(DDX_INGEST_COLLECTIONS.STAGED_ROWS)
        .where('jobId', '==', jobId);

      unsubRef.current = q.onSnapshot(
        (snap) => {
          if (cancelled) return;
          retry.clearRetry();
          setListenReconnecting(false);
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          list.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
          setRows(list);
          setLoading(false);
          setError(null);
        },
        (err) => {
          if (cancelled) return;
          console.warn('[useDdxIngestStagedRows] Firestore listen error (will retry)', err?.code, err?.message);
          setListenReconnecting(true);
          setLoading(false);
          setError(err);
          try {
            if (unsubRef.current) {
              unsubRef.current();
              unsubRef.current = null;
            }
          } catch (e) {
            /* ignore */
          }
          retry.onFailure(err);
        }
      );
    };

    retry = scheduleListenRetry(attach, () => {});
    attach();

    return () => {
      cancelled = true;
      retry.clearRetry();
      try {
        if (unsubRef.current) {
          unsubRef.current();
          unsubRef.current = null;
        }
      } catch (e) {
        /* ignore */
      }
    };
  }, [jobId]);

  const setExcludeFromPromote = useCallback(async (rowDocId, exclude) => {
    await db.collection(DDX_INGEST_COLLECTIONS.STAGED_ROWS).doc(rowDocId).update({
      excludeFromPromote: !!exclude,
    });
  }, []);

  return { rows, loading, error, listenReconnecting, setExcludeFromPromote };
}
